import { createApp, defineComponent } from "vue";
import TextInput from "../text-input/text-input.vue";

function readProps(el: HTMLElement) {
  return {
    provider: el.getAttribute("provider") || "",
    mailchimpUrl: el.getAttribute("mailchimp-url") || "",
    klaviyoUrl: el.getAttribute("klaviyo-url") || "",
    klaviyoGroup: el.getAttribute("klaviyo-group") || "",
    errorText: el.getAttribute("error-text") || "",
    successText: el.getAttribute("success-text") || "",
    privacyErrorText: el.getAttribute("privacy-error-text") || "",
  };
}

export const componentDefinition = defineComponent({
  name: "newsletter-form",
  props: [
    "provider",
    "mailchimpUrl",
    "klaviyoUrl",
    "klaviyoGroup",
    "errorText",
    "successText",
    "privacyErrorText",
  ],
  components: {
    TextInput,
    "text-input": TextInput,
  },
  data() {
    return {
      hasError: false,
      response: "",
      query: {} as Record<string, string>,
    };
  },
  computed: {
    initialQuery(): Record<string, string> {
      return {
        ...(this.provider === "klaviyo" && { g: this.klaviyoGroup }),
      };
    },
  },
  mounted() {
    this.query = { ...this.initialQuery };
  },
  methods: {
    onInputChange({ name, value }: { name: string; value: string }) {
      this.query = { ...this.query, [name]: value };
    },
    submit(event: Event) {
      event.preventDefault();

      if (
        this.$refs.privacyCheckbox &&
        !(this.$refs.privacyCheckbox as HTMLInputElement).checked
      ) {
        this.hasError = true;
        this.response = this.privacyErrorText;
        return;
      }

      const emailInput = this.$refs.emailInput as InstanceType<typeof TextInput> | undefined;
      const isValid = emailInput ? emailInput.validate() : true;

      if (!isValid) {
        this.response = "";
        this.hasError = true;
        return;
      }

      const query = Object.keys(this.query)
        .map((k) => {
          let value = this.query[k];
          if (this.provider === "mailchimp") {
            value = encodeURIComponent(value);
          }
          return `${k}=${value}`;
        })
        .join("&");

      if (this.provider === "klaviyo") {
        fetch(this.klaviyoUrl, {
          method: "POST",
          body: query,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        })
          .then((response) => {
            return response.text().then((data) => {
              this.klaviyoResponseHandler(response.status, data);
            });
          })
          .catch(() => {
            this.klaviyoResponseHandler(0, "");
          });
      }
    },
    klaviyoResponseHandler(status: number, data: string) {
      if (Number(status) !== 200) {
        this.response = this.errorText;
        this.hasError = true;
        return;
      }

      const parsed = JSON.parse(data);
      if (parsed && parsed["success"]) {
        this.hasError = false;
        this.response = this.successText;
      } else {
        this.hasError = true;
        this.response = this.errorText;
      }
    },
    isValidEmail({ value }: { value: string }) {
      return /\S+@\S+\.\S+/.test(value);
    },
  },
});

export default (el: HTMLElement) => {
  const template = el.innerHTML;
  const props = readProps(el);
  el.innerHTML = "";

  const app = createApp(
    defineComponent({
      ...(componentDefinition as any),
      template,
    }),
    props
  );
  app.component("text-input", TextInput);

  return app.mount(el);
};
